import { useAsync } from 'react-async-hook'
import { CacheFactory, type Cache } from '../api/v1/client/ui/services/cache'
import { useApiClient } from './api-client'
import type { ApiClientV1 } from '../api'

export interface InstalledPackage {
  name: string
  status: string
  latestVersion?: string
  installedDate?: string
  metadata?: {
    components?: {
      functions?: {
        items?: string[]
      }
    }
  }
}

export interface IFetchInstalledPackagesOptions {
  bypassCache?: boolean
}

export interface IUseFunctionInstalledOptions {
  bypassCache?: boolean
}

export interface UseFunctionInstalledResult {
  installed: boolean | undefined
  loading: boolean
  error: Error | undefined
}

// The endpoint takes no params, but responses differ per tenant, so the
// cache key carries the tenant ID to keep results scoped to their client.
type InstalledPackagesCacheKey = { tenantId: string }

// In-memory only, no SWR: keeps the "N callers, one request" contract
// deterministic (SWR/persistence add async gaps that let concurrent callers
// each miss the cache and fire their own request).
let cacheInstance: Cache<InstalledPackagesCacheKey, InstalledPackage[]> | null =
  null

const getInstalledPackagesCache = (): Cache<
  InstalledPackagesCacheKey,
  InstalledPackage[]
> => {
  if (!cacheInstance) {
    cacheInstance = CacheFactory.create<
      InstalledPackagesCacheKey,
      InstalledPackage[]
    >('installed-packages', false, false)
  }
  return cacheInstance
}

/**
 * Reset the cache instance (primarily for testing).
 */
export const resetInstalledPackagesCacheInstance = () => {
  cacheInstance = null
}

/**
 * Fetches the list of installed packages from the platform API.
 * This function can be used outside of React components.
 *
 * Results are cached so repeated calls on the same page share one request.
 * Pass `bypassCache: true` to skip the cache and always fetch fresh data.
 *
 * @param apiClient - The API client instance
 * @param options - Options for cache control
 * @param options.bypassCache - When true, skips the cache and fetches fresh data. Defaults to false.
 * @returns The list of installed packages
 */
export const fetchInstalledPackages = async (
  apiClient: ApiClientV1,
  options: IFetchInstalledPackagesOptions = {},
): Promise<InstalledPackage[]> => {
  const { bypassCache = false } = options

  const fetch = () =>
    apiClient.services.apiServer.get<InstalledPackage[]>('/pkg/installed')

  if (bypassCache) {
    return fetch()
  }

  const cache = getInstalledPackagesCache()
  return cache.execute({ tenantId: apiClient.tenantId }, fetch)
}

/**
 * Determines whether a connected function is installed, given a list of
 * installed packages. Matches against the package's declared function
 * items, falling back to a package-name match when items aren't reported.
 */
export const isFunctionInstalled = (
  packages: InstalledPackage[] | null | undefined,
  functionName: string,
): boolean => {
  if (!Array.isArray(packages)) {
    return false
  }

  return packages.some((pkg) => {
    if (!pkg || pkg.status !== 'Installed') {
      return false
    }
    const items = pkg.metadata?.components?.functions?.items
    return Array.isArray(items)
      ? items.includes(functionName)
      : pkg.name === functionName
  })
}

/**
 * Hook to check whether a connected function is installed on this tenant,
 * via `GET /pkg/installed`. Lets components that depend on a package
 * function (e.g. an ingestion function from another package) degrade
 * gracefully when it's missing.
 *
 * The installed packages list is shared across callers via a module-level
 * cache, so multiple components checking different function names on the
 * same page only trigger one request.
 *
 * @param functionName - The connected function name to check
 * @param options - Options for cache control
 * @param options.bypassCache - When true, skips the cache and fetches fresh data. Defaults to false.
 * @returns An object with the installed verdict (undefined while loading or on error), loading state, and error
 *
 * @example
 * ```tsx
 * const { installed, loading } = useFunctionInstalled('timesheets')
 *
 * if (loading) return <div>Loading...</div>
 * if (!installed) return <div>Feature unavailable</div>
 * return <TimesheetsConsole />
 * ```
 */
export const useFunctionInstalled = (
  functionName: string,
  options: IUseFunctionInstalledOptions = {},
): UseFunctionInstalledResult => {
  const { bypassCache = false } = options
  const apiClient = useApiClient()
  const { result, loading, error } = useAsync(
    (client: ApiClientV1, bypass: boolean) =>
      fetchInstalledPackages(client, { bypassCache: bypass }),
    [apiClient, bypassCache],
  )

  const installed =
    loading || !result ? undefined : isFunctionInstalled(result, functionName)

  return { installed, loading, error }
}
